import { Link } from "react-router-dom";
import { CheckCircle2, Phone, ExternalLink, ShieldCheck } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { brand, founder } from "@/data/site/brand";
import { Reveal } from "@/components/motion/Reveal";

const FranklinSofi = () => (
  <SiteLayout>
    <PageSEO
      title={`${founder.name}, ${founder.credentials} — Founder`}
      description={`${founder.name} is the founder of Comprehensive Tax Resolution LLC — a CPA, CTRS, and ASTPS Gold Member with 15+ years of IRS representation experience.`}
      path="/about/franklin-sofi"
      image={founder.image}
      ogType="profile"
      breadcrumbs={[
        { name: "Home", href: "/" },
        { name: "About", href: "/about" },
        { name: founder.name, href: "/about/franklin-sofi" },
      ]}
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `${brand.siteUrl}/about/franklin-sofi#person`,
        name: founder.name,
        givenName: "Franklin",
        familyName: "Sofi",
        jobTitle: founder.title,
        description: founder.bio,
        image: `${brand.siteUrl}${founder.image}`,
        url: `${brand.siteUrl}/about/franklin-sofi`,
        telephone: brand.phoneTel,
        email: brand.email,
        worksFor: {
          "@type": "Organization",
          name: brand.legalName,
          url: brand.siteUrl,
          telephone: brand.phoneTel,
          address: {
            "@type": "PostalAddress",
            streetAddress: brand.address.street,
            addressLocality: brand.address.city,
            addressRegion: brand.address.region,
            postalCode: brand.address.postalCode,
            addressCountry: brand.address.country,
          },
        },
        memberOf: [
          { "@type": "Organization", name: "American Institute of Certified Public Accountants (AICPA)" },
          { "@type": "Organization", name: "American Society of Tax Problem Solvers (ASTPS)", description: "Gold Member" },
          { "@type": "Organization", name: "Texas Society of Certified Public Accountants" },
        ],
        sameAs: founder.sameAs,
        hasCredential: founder.credentialsList.map((c) => ({
          "@type": "EducationalOccupationalCredential",
          name: c,
          credentialCategory: "professional certification",
        })),
        knowsAbout: [
          "Offer in Compromise",
          "IRS Installment Agreement",
          "Wage Garnishment Release",
          "Bank Levy Release",
          "IRS Audit Representation",
          "Trust Fund Recovery Penalty",
          "Penalty Abatement",
        ],
      }}
    />

    <section className="relative bg-gradient-navy text-white overflow-hidden">
      <HeroBackground image={stockImages.consultation} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl grid lg:grid-cols-[1fr_auto] gap-10 items-center">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
            Founder bio
          </p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-4 [text-wrap:balance]">
            {founder.name},<br />
            <span className="text-gold">{founder.credentials}</span>
          </h1>
          <p className="text-lg text-white/80">{founder.title}</p>
        </Reveal>
        <Reveal direction="left">
          <img
            src={founder.image}
            alt={`${founder.name}, ${founder.credentials}`}
            className="w-48 h-48 lg:w-60 lg:h-60 rounded-full object-cover object-top border-4 border-gold shadow-2xl justify-self-center"
           width={240} height={240} />
        </Reveal>
      </div>
    </section>

    <section className="py-16 lg:py-32 bg-white">
      <div className="container mx-auto px-4 max-w-5xl grid lg:grid-cols-[1.6fr_1fr] gap-16">
        <article>
          <p className="text-[17px] leading-[1.75] text-foreground/85 mb-10">{founder.bio}</p>

          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">Background</p>
          <p className="text-foreground/85 leading-[1.75] text-[17px] mb-10">
            Franklin built {brand.legalName} after spending years inside large tax-resolution
            operations and watching how often taxpayers were sold programs they didn't qualify for.
            He launched the firm with a different model: every client works directly with a
            credentialed CPA, every plan is based on actual IRS financial standards, and every
            engagement carries a clear flat fee.
          </p>

          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
            Practice areas
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-10">
            {[
              "Offer in Compromise (Form 656 / 433-A(OIC))",
              "Installment Agreements & Partial Pay",
              "Currently Not Collectible (CNC)",
              "Wage garnishment & bank levy releases",
              "IRS audit representation & appeals",
              "Trust Fund Recovery Penalty defense",
              "Payroll tax (941/940) resolution",
              "Penalty abatement",
            ].map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-foreground/85">
                <CheckCircle2 className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> {p}
              </li>
            ))}
          </ul>

          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
            Memberships
          </p>
          <ul className="space-y-2.5">
            <li className="text-foreground/85">American Institute of Certified Public Accountants (AICPA)</li>
            <li className="text-foreground/85">
              American Society of Tax Problem Solvers (ASTPS) —{" "}
              <strong className="text-navy">Gold Member</strong>
            </li>
            <li className="text-foreground/85">Texas Society of Certified Public Accountants</li>
          </ul>
        </article>

        <aside className="space-y-10 lg:border-l lg:border-border lg:pl-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
              Credentials
            </p>
            <ul className="space-y-2.5">
              {founder.credentialsList.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-foreground/85">
                  <CheckCircle2 className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Verify my credentials
            </p>
            <p className="text-sm text-foreground/75 mb-4 leading-relaxed">
              Independently verify every credential through the issuing body.
            </p>
            <ul className="space-y-2.5">
              {founder.verifyLinks.map((v) => (
                <li key={v.href}>
                  <a
                    href={v.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-2 text-sm text-navy hover:text-gold font-semibold leading-snug"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mt-0.5 text-gold shrink-0" />
                    <span>{v.org}</span>
                  </a>
                </li>
              ))}
            </ul>
            <Link
              to="/editorial-standards"
              className="inline-block mt-4 text-xs font-semibold text-navy hover:text-gold border-b border-dotted border-navy/40"
            >
              Read our editorial standards →
            </Link>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
              Talk to Franklin's team
            </p>
            <p className="text-sm text-foreground/75 mb-4 leading-relaxed">
              Free, confidential case review with a CPA.
            </p>
            <a
              href={`tel:${brand.phoneTel}`}
              className="flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold py-3.5 rounded"
            >
              <Phone className="w-4 h-4" /> {brand.phone}
            </a>
            <Link
              to="/contact"
              className="block text-center mt-3 text-sm text-navy hover:text-gold font-semibold"
            >
              Or use the contact form →
            </Link>
          </div>
        </aside>
      </div>
    </section>
  </SiteLayout>
);

export default FranklinSofi;
